import { cloneDeep } from 'lodash';
import { getCountsWithComma } from '../../../../commons/util/utilities';
import { capitalizeFirstChar } from '../../../../utils/factory';
import { buildChartTooltip } from '../chartTooltip';

// Helper to format calendar types for labels
const getCalendarTypes = (data) => data.map(({ type }) => (
  (type === 'ics_other' && 'ICS') ||
  (type === 'windowslive' && 'Outlook.com (Web)') ||
  (type === 'office365' && 'Microsoft 365') ||
  (!type && 'Other') ||
  capitalizeFirstChar(type)
));

// Helper to get labels from data (Keep this if it's reused)
export const getLabels = (data) => data?.map(({ name }) => name);

// Helper to get values from data (Keep this if it's reused)
export const getValues = (data) => data?.map(({ count }) => count);

// --- CORE LOGIC: Process and Aggregate Data ---
// Inside helperEffects.js (Ensure this code replaces the existing function)

export function processChartData(data, type) {
    if (!data || data.length === 0) return { sortedData: [], totalCount: 0 };

    // Calendar type is simpler—just sorting. Deep clone first so the
    // in-place mutation done by `getCountsWithComma` downstream can't corrupt
    // MobX-observed `count` values across renders.
    if (type === 'calendar') {
        const sortedData = cloneDeep(data).sort((a, b) => b.count - a.count);
        const totalCount = sortedData.reduce((prev, cur) => prev + cur.count, 0);
        return { sortedData, totalCount };
    }

    // --- Device Type Aggregation Logic ---
    
    // 💡 CRITICAL FIX: Deep clone the data before any mutation. This breaks the loop.
    const mutableDevices = cloneDeep(data); 

    const deviceTypesList = [
        { 'type': 'Mobile', 'name': 'Mobile', 'count': 0 },
        { 'type': 'Desktop', 'name': 'Desktop', 'count': 0 },
        { 'type': 'Tablet', 'name': 'Tablet', 'count': 0 },
    ];
    
    // Helper to sum counts (assumes getTotalCount is a global helper)
    const getTotalCount = (dataToFilter) => {
        if (dataToFilter.length === 0) return 0;
        return dataToFilter.reduce(function (prev, cur) {
            return prev + (cur.count || 0); // Ensure count is a number
        }, 0);
    };

    // Filter Devices that match Mobile, Desktop, Tablet
    const filteredDevices = mutableDevices.filter(function (o1) {
        return deviceTypesList.some(function (o2) {
            o1.name = o2.name; 
            return o2.type.toLowerCase() === o1.type.toLowerCase();
        });
    });

    // Identify 'Other' Items (Items not Mobile/Desktop/Tablet)
    const otherItemsDevices = mutableDevices.filter(function (o1) {
        return !deviceTypesList.some(function (o2) {
            // MUTATION: Renames the 'other' devices
            o1.name = 'other';
            return o1.type.toLowerCase() === o2.type.toLowerCase() ||
                o1.type.toLowerCase() === 'mobile' ||
                o1.type.toLowerCase() === 'phone';
        });
    });
    
    // 3. Identify specific 'Phone' items for aggregation
    const phoneFilteredData = mutableDevices.filter(function (o1) {
        return deviceTypesList.some(function () {
            o1.name = 'Phone';
            return o1.type.toLowerCase() === 'phone';
        });
    });
    
    // 4. Calculate Totals
    const otherDevicesTotal = getTotalCount(otherItemsDevices);
    const phoneDevicesTotal = getTotalCount(phoneFilteredData);

    // 5. Aggregate Totals into Filtered List
    const deviceDataFiltered = filteredDevices.map((a) => {
        // MUTATION 3 & 4 (Safe, as we are creating a new mapped array/object)
        a.count = a.type === 'Desktop' ? a.count + otherDevicesTotal : a.count;
        a.count = (a.type === 'Mobile') ? a.count + phoneDevicesTotal : a.count;
        a.name = a.type === 'Phone' ? 'Mobile' : a.type;
        return a;
    });

    // Enforce Mobile → Desktop → Tablet order so the positional `colors` array
    // in defaultDeviceThemeOptions maps to the correct device.
    const deviceOrder = deviceTypesList.map((d) => d.type);
    deviceDataFiltered.sort((a, b) => deviceOrder.indexOf(a.type) - deviceOrder.indexOf(b.type));

    const totalCount = getTotalCount(deviceDataFiltered);

    return { sortedData: deviceDataFiltered, totalCount };
}

// --- CORE LOGIC: Format Data into Options ---
export function formatChartOptions(processedData, themeOptions, type) {
    // Note: Assuming getValues and getCountsWithComma are accessible/imported
    const sortedData = processedData.sortedData;
    const sortedValues = getValues(sortedData);
    
    // Ensure data is formatted for tooltips
    const formattedData = getCountsWithComma(sortedData);
    
    const labels = type === 'calendar' 
        ? getCalendarTypes(sortedData)
        : getLabels(sortedData);

    // Construct the final options object, merging themeOptions
    const finalOptions = {
        ...themeOptions,
        labels: labels,
        tooltip: {
            custom: ({ seriesIndex, w }) => {
                const point = formattedData?.[seriesIndex];
                if (!point) return '';
                return buildChartTooltip({
                    label: labels?.[seriesIndex] ?? '',
                    count: point.count,
                    sublabel: 'Subs',
                    color: w?.globals?.colors?.[seriesIndex],
                });
            },
        },
    };
 
    //💡 CRITICAL FIX: Delete the buggy 'responsive' property from the FINAL object CAUSES THE Maximum Range Error issue to keep rendering loop
    if (finalOptions.responsive) {
        delete finalOptions.responsive;
    }

    //Return the stable objects derived from the calculation
    return {
        series: sortedValues,
        options: finalOptions // 💡 Return the cleaned 'finalOptions' object
    };
}
